import React, { useContext, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Cropper from "../../../../src/cropper/components/CroppersWizard.tsx";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { Language } from '../../../App';
import { RiImageAddLine, RiSparkling2Fill } from 'react-icons/ri';
import { IoCloseSharp, IoTimeOutline, IoTrashOutline } from 'react-icons/io5';
import { AiOutlineEdit } from 'react-icons/ai';
import { adminBaseURL } from '../../../constants/API';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getToken } from '../../../shared/token';
import ImageShowModal from '../../common/ImageShowModal.jsx';
import { useCheckSubscriptionPermission } from '../../../hooks/useCheckSubscriptionPermission';
import MixpanelManager from '../../../constants/MixpanelManager';
import { MixpanelEvents } from '../../../constants/MixpanelEvents';
import './formTheme.css';

function PollTab({ onHide, setProgress, handleApiCall }) {
  const { lang } = useContext(Language);
  const checkPermission = useCheckSubscriptionPermission('upload_poll');
  const [options, setOptions] = useState(['', '']);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [view, setView] = useState('create');

  // States for Image Preview in Modal
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const toggleImagePreview = () => setIsImagePreviewOpen(!isImagePreviewOpen);
  const handleImageClick = (img) => {
    setPreviewImage(img);
    toggleImagePreview();
  };

  const durationsList = [
    { id: lang?.one_day || '1 Day (24 hours)', value: '1 Day' },
    { id: lang?.three_days || '3 Days (72 hours)', value: '3 Days' },
    { id: lang?.seven_days || '7 Days (1 week)', value: '7 Days' },
    { id: lang?.two_weeks || '2 Weeks (14 days)', value: '2 Weeks' },
  ];

  const [images, setImages] = useState([]);
  const [coverImg, setCoverImg] = useState([]);
  const [cropperOpen, setCropperOpen] = useState(false);
  const maxNumber = 2;
  const [ar, setAR] = useState(1);
  const [maxARatio, setMaxAR] = React.useState(undefined);
  const [minARatio, setMinAR] = React.useState(undefined);
  const [resizable, setResizable] = useState(true);
  const [hasSettings, setHasSettings] = useState(false);
  const [updateCoordinatesCheck, setUpdateCoordinatesCheck] = useState(false);
  const [fileUploadRefs, setFileUploadRefs] = React.useState({});
  const [numberOfImages, setNumberOfImages] = React.useState(
    (Array.isArray(coverImg) ? coverImg.length : Object.keys(coverImg || {}).length) < maxNumber
      ? (Array.isArray(coverImg) ? coverImg.length : Object.keys(coverImg || {}).length)
      : maxNumber
  );

  const [height, setHeight] = React.useState(undefined);
  const [width, setWidth] = React.useState(undefined);
  const [top, setTop] = React.useState(undefined);
  const [left, setLeft] = React.useState(undefined);

  const handleAddOption = () => {
    if (options.length < 4) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setOptions(updatedOptions);
  };

  const handleClose = () => {
    setCropperOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      question: '',
      option_1: '',
      option_2: '',
      selectedDuration: '',
    },
    onSubmit: async (values) => {
      await handleSubmit(values);
      setOptions([values.option_1, values.option_2]);
      formik.resetForm({
        question: '',
        option_1: '',
        option_2: '',
        selectedDuration: '',
      });
    },
    validationSchema: yup.object({
      question: yup.string().required(lang?.please_enter_your_question || "Please enter your question"),
      option_1: yup.string().required(lang?.please_enter_option_1 || "Please enter option 1"),
      option_2: yup.string().required(lang?.please_enter_option_2 || "Please enter option 2"),
      selectedDuration: yup.string().required(lang?.please_select_duration || "Please select duration"),
    }),
  });

  const handleDurationChange = (event) => {
    setSelectedDuration(event.target.value);
    formik.setFieldValue("selectedDuration", event.target.value);
  };

  const handleSubmit = async (values) => {
    if (!checkPermission()) {
      return;
    }
    onHide();
    await handleApiCall(() => uploadPoll(values), { type: "poll" });
  };

  const uploadPoll = async (data) => {
    const coverList = Array.isArray(coverImg) ? coverImg : Object.values(coverImg || {});
    let newData = {
      title: data.question,
      option_1: options[0],
      option_2: options[1],
      option_3: options[2] || "",
      option_4: options[3] || "",
      type: "poll",
      caption: "...",
      duration: selectedDuration,
      media_base64: coverList[0],
    };
    try {
      const res = await axios.post(adminBaseURL + `user/post/poll/create`, newData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getToken()}`,
        },
        onUploadProgress: (progressData) => {
          setProgress(Math.round((100 * progressData.loaded) / progressData.total));
        },
      });
      if (res.data.status) {
        MixpanelManager.track(MixpanelEvents.createPost, {
          post_type: 'poll',
          options_count: options.filter((o) => o.trim()).length,
          has_image: coverList.length > 0,
          duration: selectedDuration,
        });
        onHide();
        return res;
      } else {
        toast.error(res.data.error);
      }
    } catch (error) {
      toast.error("Upload failed, please try again.");
    }
  };

  const handleEditClick = () => {
    setView('create');
  };

  const onImageRemove = (index) => {
    const updatedImages = Array.isArray(coverImg) ? [...coverImg] : Object.values(coverImg || {});
    const updatedFileUploadRefs = { ...fileUploadRefs };
    updatedImages.splice(index, 1);
    if (fileUploadRefs[index + 1] && fileUploadRefs[index + 1].current) {
      fileUploadRefs[index + 1].current.value = '';
    }
    setImages(updatedImages);
    setCoverImg(updatedImages);
    const newFileUploadRefs = {};
    Object.keys(updatedFileUploadRefs)
      .filter((key) => key !== index.toString())
      .forEach((key, newIndex) => {
        newFileUploadRefs[newIndex] = updatedFileUploadRefs[key];
      });

    setFileUploadRefs(newFileUploadRefs);
    if (updatedImages.length === 0) {
      setHasSettings(true);
      setAR(undefined);
      setMinAR(undefined);
      setMaxAR(undefined);
      setWidth(500);
      setHeight(500);
      setTop(0);
      setLeft(0);
      setCropperOpen(false);
    }
  };

  const handleDoneClick = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setOptions([formik.values.option_1, formik.values.option_2, ...options.slice(2)]);
      setView('preview');
    } else {
      formik.setTouched({
        question: true,
        option_1: true,
        option_2: true,
        selectedDuration: true,
      });
    }
  };

  const handleImageUploadClick = () => {
    const currentIndex = numberOfImages - 1;
    if (fileUploadRefs[currentIndex] && fileUploadRefs[currentIndex].current) {
      fileUploadRefs[currentIndex].current.value = '';
      fileUploadRefs[currentIndex].current.click();
    }
  };

  return (
    <>
      {view === 'create' ? (
        <div className="pf-form-body">
          {/* AI Banner */}
          <div className="pf-ai-banner">
            <div className="pf-ai-banner-left">
              <RiSparkling2Fill className="pf-ai-banner-sparkle" size={15} />
              <span>Interactive Clinical Consensus & Polling</span>
            </div>
            <span className="pf-ai-banner-tag">Live Analytics</span>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {/* Question Field */}
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">Clinical Question or Case Query</span>
                <span className="pf-char-counter">{formik.values.question.length}/200</span>
              </div>
              <textarea
                className={`pf-textarea ${formik.touched.question && formik.errors.question ? "pf-invalid" : ""}`}
                placeholder={lang?.enter_question || "Ask a clinical question, diagnostic dilemma, or survey topic..."}
                id="question"
                name="question"
                rows={2}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.question}
                maxLength={200}
              />
              {formik.touched.question && formik.errors.question && (
                <span className="pf-error-text">{formik.errors.question}</span>
              )}
            </div>

            {/* Poll Choices Card Section */}
            <div className="pf-card-section">
              <div className="d-flex align-items-center justify-content-between">
                <span className="fs-12 fw-600" style={{ color: "#0B132B" }}>Poll Choices & Answers</span>
                <span className="fs-11 fw-500" style={{ color: "#64748B" }}>2 to 4 options</span>
              </div>

              {/* Option 1 */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: "var(--pf-accent-light)",
                    color: "var(--pf-accent-dark)",
                    fontSize: "11px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  1
                </span>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    className={`pf-input ${formik.touched.option_1 && formik.errors.option_1 ? "pf-invalid" : ""}`}
                    placeholder={lang?.add_option || "Choice 1..."}
                    id="option_1"
                    name="option_1"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.option_1}
                  />
                </div>
              </div>
              {formik.touched.option_1 && formik.errors.option_1 && (
                <span className="pf-error-text" style={{ paddingLeft: "30px" }}>{formik.errors.option_1}</span>
              )}

              {/* Option 2 */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: "var(--pf-accent-light)",
                    color: "var(--pf-accent-dark)",
                    fontSize: "11px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  2
                </span>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    className={`pf-input ${formik.touched.option_2 && formik.errors.option_2 ? "pf-invalid" : ""}`}
                    placeholder={lang?.add_option || "Choice 2..."}
                    id="option_2"
                    name="option_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.option_2}
                  />
                </div>
              </div>
              {formik.touched.option_2 && formik.errors.option_2 && (
                <span className="pf-error-text" style={{ paddingLeft: "30px" }}>{formik.errors.option_2}</span>
              )}

              {/* Additional Options 3 & 4 */}
              {options.slice(2).map((option, index) => (
                <div key={index} className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      background: "var(--pf-accent-light)",
                      color: "var(--pf-accent-dark)",
                      fontSize: "11px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {index + 3}
                  </span>
                  <div style={{ flex: 1, position: "relative" }}>
                    <input
                      type="text"
                      className="pf-input"
                      value={option}
                      onChange={(e) => {
                        const updatedOptions = [...options];
                        updatedOptions[index + 2] = e.target.value;
                        setOptions(updatedOptions);
                      }}
                      placeholder={`Choice ${index + 3} (Optional)...`}
                      style={{ paddingRight: "34px" }}
                    />
                    <button
                      type="button"
                      className="d-flex align-items-center justify-content-center border-0 bg-transparent"
                      style={{
                        color: '#EF4444',
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                      onClick={() => handleRemoveOption(index + 2)}
                    >
                      <IoTrashOutline size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {options.length < 4 && (
                <div>
                  <button
                    type="button"
                    className="pf-chip-btn"
                    onClick={handleAddOption}
                  >
                    <FaPlus size={10} color="#F59E0B" />
                    <span>{lang?.add_more_option || "Add Choice"}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Poll Duration Field */}
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">
                  <IoTimeOutline size={13} color="#F59E0B" />
                  {lang?.set_poll_duration || "Poll Duration"}
                </span>
              </div>
              <select
                className="pf-select"
                value={selectedDuration}
                onChange={handleDurationChange}
                style={{ color: selectedDuration === "" ? "#94A3B8" : "#0B132B" }}
              >
                <option value="">{lang?.select_duration || "Select poll duration"}</option>
                {durationsList.map((duration, index) => (
                  <option key={index} value={duration.value}>
                    {duration.id}
                  </option>
                ))}
              </select>
              {formik.touched.selectedDuration && formik.errors.selectedDuration && (
                <span className="pf-error-text">{formik.errors.selectedDuration}</span>
              )}
            </div>

            {/* Cover Image Upload (Dropzone if Empty, Thumbnails if Uploaded) */}
            {(() => {
              const coverList = Array.isArray(coverImg) ? coverImg : Object.values(coverImg || {});
              return coverList.length === 0 ? (
                <div className="pf-upload-zone" onClick={handleImageUploadClick}>
                  <div className="pf-upload-zone-left">
                    <div className="pf-upload-icon-circle">
                      <RiImageAddLine size={18} />
                    </div>
                    <div>
                      <div className="pf-upload-zone-title">Add Case Reference Image</div>
                      <div className="pf-upload-zone-sub">Attach an ECG, scan, or diagnostic image to accompany the poll</div>
                    </div>
                  </div>
                  <button type="button" className="pf-chip-btn">
                    Attach
                  </button>
                </div>
              ) : (
                <div className="pf-thumb-strip">
                  {coverList.map((img, index) => (
                    <div key={index} className="pf-thumb" style={{ width: "95px", height: "95px" }}>
                      <PhotoProvider speed={() => 800}>
                        <PhotoView src={img}>
                          <img
                            className="cursor"
                            onClick={() => handleImageClick(img)}
                            src={img}
                            alt="Uploaded"
                            style={{ width: "95px", height: "95px", objectFit: "cover", display: "block" }}
                          />
                        </PhotoView>
                      </PhotoProvider>
                      <button
                        type="button"
                        className="pf-thumb-remove"
                        aria-label="Remove"
                        onClick={() => onImageRemove(index)}
                      >
                        <IoCloseSharp size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Footer Row (Clean cancel & preview only) */}
            <div className="pf-footer-row" style={{ justifyContent: "flex-end" }}>
              <div className="pf-footer-actions">
                <button type="button" className="pf-btn-cancel" onClick={onHide}>
                  {lang?.cancel || "Cancel"}
                </button>
                <button
                  type="button"
                  disabled={!formik.values.option_1 || !formik.values.question || !formik.values.option_2 || !selectedDuration}
                  onClick={handleDoneClick}
                  className="pf-btn-submit"
                >
                  <span>{lang?.done_post || 'Preview Poll'}</span>
                </button>
              </div>
            </div>
          </form>

          <Cropper
            images={images}
            setImages={setImages}
            imagesArr={coverImg}
            setImagesArr={setCoverImg}
            value={coverImg}
            max={maxNumber}
            allowCrop={true}
            ar={ar}
            setAR={setAR}
            minARatio={minARatio}
            setMaxAR={setMaxAR}
            maxARatio={maxARatio}
            setMinAR={setMinAR}
            resizable={resizable}
            setResizable={setResizable}
            handleClose={handleClose}
            fileUploadRefs={fileUploadRefs}
            setFileUploadRefs={setFileUploadRefs}
            numberOfImages={numberOfImages}
            setNumberOfImages={setNumberOfImages}
            hasSettings={hasSettings}
            setHasSettings={setHasSettings}
            updateCoordinatesCheck={updateCoordinatesCheck}
            setUpdateCoordinatesCheck={setUpdateCoordinatesCheck}
            height={height}
            setHeight={setHeight}
            width={width}
            setWidth={setWidth}
            top={top}
            setTop={setTop}
            left={left}
            setLeft={setLeft}
            cropperOpen={cropperOpen}
            setCropperOpen={setCropperOpen}
          />
        </div>
      ) : (
        /* Preview Review View */
        <div className="pf-form-body">
          <div className="pf-ai-banner">
            <div className="pf-ai-banner-left">
              <RiSparkling2Fill className="pf-ai-banner-sparkle" size={15} />
              <span>Poll Preview — Ready for Publishing</span>
            </div>
            <button
              type="button"
              className="pf-chip-btn"
              onClick={handleEditClick}
              style={{ padding: "3px 10px", fontSize: "11px" }}
            >
              <AiOutlineEdit size={12} color="#F59E0B" /> {lang?.edit || "Edit"}
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="pf-card-section">
              <h5
                className="mb-0"
                style={{
                  color: "#0B132B",
                  fontSize: "14.5px",
                  fontWeight: 600,
                  lineHeight: "1.35",
                }}
              >
                {formik.values.question}
              </h5>

              <div className="d-flex flex-column gap-1.5 mt-1">
                {options
                  .filter((option) => option !== null && option !== "")
                  .map((option, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "9px 13px",
                        borderRadius: "10px",
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        color: "#0B132B",
                        fontSize: "13px",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          background: "var(--pf-accent-light)",
                          color: "var(--pf-accent-dark)",
                          fontSize: "11px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </span>
                      <span style={{ flex: 1 }}>{option}</span>
                    </div>
                  ))}
              </div>

              {(Array.isArray(coverImg) ? coverImg[0] : (coverImg && Object.values(coverImg)[0])) && (
                <div className="pf-thumb mt-1" style={{ maxWidth: "200px", borderRadius: "10px" }}>
                  <img
                    src={Array.isArray(coverImg) ? coverImg[0] : Object.values(coverImg)[0]}
                    style={{ width: "100%", display: "block" }}
                    alt="Poll cover"
                  />
                </div>
              )}

              <div
                className="d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded-pill mt-1"
                style={{
                  fontSize: "11.5px",
                  color: "var(--pf-accent-dark)",
                  fontWeight: 600,
                  background: "var(--pf-accent-light)",
                  border: "1px solid var(--pf-accent-border)",
                  width: "fit-content",
                }}
              >
                <IoTimeOutline size={13} color="#F59E0B" />
                <span>Active for {selectedDuration}</span>
              </div>
            </div>

            <div className="pf-footer-row" style={{ justifyContent: "flex-end" }}>
              <div className="pf-footer-actions">
                <button type="button" className="pf-btn-cancel" onClick={onHide}>
                  {lang?.cancel || "Cancel"}
                </button>
                <button type="submit" className="pf-btn-submit">
                  <span>{lang?.publish || "Publish Poll"}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <ImageShowModal
        isOpen={isImagePreviewOpen}
        toggle={toggleImagePreview}
        imageSrc={previewImage}
      />
    </>
  );
}

export default PollTab;
